import { useEffect, useState } from 'react';
import { ActivityIndicator, Alert, View } from 'react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { doc, getDoc } from 'firebase/firestore';

import { Screen } from '@/components/layout/Screen';
import { Button } from '@/components/ui/Button';
import { Body, SubHeading } from '@/components/ui/Text';
import { useTheme } from '@/theme';
import { db } from '@/lib/firebase';
import { checkAndAcceptPendingInvitations } from '@/services/pendingCollaboration';
import { setPendingNavigationIntent } from '@/navigation/pendingNavigationIntent';
import { useUserStore } from '@/state/userStore';

/**
 * Web + native landing for collaborator emails (`/join-collaboration?invite=...`).
 * Kept under `join-collaboration/index.tsx` so Expo Router reliably registers the segment on static web.
 */
function normalizeInviteParam(raw: string | string[] | undefined): string | undefined {
  if (!raw) {
    return undefined;
  }
  const value = Array.isArray(raw) ? raw[0] : raw;
  const trimmed = String(value ?? '').trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

function buildInviteLeadCopy(opts: { inviterName?: string; tripName?: string }): string {
  const inviter = opts.inviterName?.trim();
  const trip = opts.tripName?.trim();
  if (inviter && trip) {
    return `${inviter} has invited you to collaborate on the ${trip} trip on Tripvio.`;
  }
  if (trip) {
    return `You're invited to collaborate on the ${trip} trip on Tripvio.`;
  }
  if (inviter) {
    return `${inviter} has invited you to collaborate on Tripvio.`;
  }
  return "You're invited to collaborate on a trip on Tripvio.";
}

export default function JoinCollaborationScreen() {
  const theme = useTheme();
  const router = useRouter();
  const inviteParam = useLocalSearchParams<{
    invite?: string | string[];
    from?: string | string[];
    trip?: string | string[];
  }>();
  const inviteId = normalizeInviteParam(inviteParam.invite);
  const inviterHint = normalizeInviteParam(inviteParam.from);
  const tripHint = normalizeInviteParam(inviteParam.trip);

  const authReady = useUserStore((s) => s.authReady);
  const isAuthenticated = useUserStore((s) => s.isAuthenticated);
  const firebaseUser = useUserStore((s) => s.firebaseUser);

  const [finalizeError, setFinalizeError] = useState<string | undefined>();
  const [retryNonce, setRetryNonce] = useState(0);

  useEffect(() => {
    if (!inviteId || !authReady || !isAuthenticated || !firebaseUser?.uid) {
      return;
    }
    const userEmail = firebaseUser.email;
    if (!userEmail) {
      return;
    }

    let cancelled = false;

    void (async () => {
      const emailLower = userEmail.toLowerCase().trim();
      try {
        setFinalizeError(undefined);
        await checkAndAcceptPendingInvitations(firebaseUser.uid, emailLower);

        let tripId: string | undefined;
        try {
          const snap = await getDoc(doc(db, 'pendingInvitations', inviteId));
          if (snap.exists()) {
            const data = snap.data() as Record<string, unknown> | undefined;
            tripId = typeof data?.tripId === 'string' ? data.tripId : undefined;
          }
        } catch {
          tripId = undefined;
        }

        if (cancelled) {
          return;
        }

        if (tripId) {
          router.replace({
            pathname: '/(tabs)/plan',
            params: { tripId },
          });
        } else {
          Alert.alert(
            'Almost there',
            'We synced your collaborator invite — check Plan for your trips. If nothing appears right away, give it a moment.',
          );
          router.replace('/(tabs)/plan');
        }
      } catch (error) {
        if (cancelled) {
          return;
        }
        const message =
          error instanceof Error
            ? error.message
            : typeof error === 'string'
              ? error
              : 'Something went wrong while applying your invitation.';
        setFinalizeError(message);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [authReady, firebaseUser?.email, firebaseUser?.uid, inviteId, isAuthenticated, retryNonce, router]);

  const handleContinueToSignIn = () => {
    if (!inviteId) {
      return;
    }
    setPendingNavigationIntent({
      kind: 'route',
      pathname: '/join-collaboration',
      params: {
        invite: inviteId,
        ...(inviterHint ? { from: inviterHint } : {}),
        ...(tripHint ? { trip: tripHint } : {}),
      },
    });
    router.replace('/(onboarding)/sign-in');
  };

  const handleCloseOrHome = () => {
    router.replace('/');
  };

  const handleRetry = () => {
    setFinalizeError(undefined);
    setRetryNonce((n) => n + 1);
  };

  if (!inviteId) {
    return (
      <Screen title="Invitation" showOfflineIndicator={false} showBackButton onBack={handleCloseOrHome}>
        <SubHeading>Missing invitation</SubHeading>
        <Body>Open the link from your Tripvio collaborator email, or ask the person who invited you to send it again.</Body>
      </Screen>
    );
  }

  if (!authReady) {
    return (
      <Screen
        title="Invitation"
        scrollable={false}
        showOfflineIndicator={false}
        showBackButton
        onBack={handleCloseOrHome}
      >
        <View style={{ alignItems: 'center', justifyContent: 'center', flex: 1, paddingVertical: theme.spacing(3) }}>
          <ActivityIndicator size="large" color={theme.colors.primary} accessibilityLabel="Loading" />
          <Body style={{ marginTop: theme.spacing(2), opacity: 0.85 }}>Loading Tripvio…</Body>
        </View>
      </Screen>
    );
  }

  if (!isAuthenticated || !firebaseUser?.email) {
    const lead = buildInviteLeadCopy({ inviterName: inviterHint, tripName: tripHint });
    return (
      <Screen
        title="Invitation"
        showOfflineIndicator={false}
        scrollable={false}
        showBackButton
        onBack={handleCloseOrHome}
      >
        <View style={{ gap: theme.spacing(2) }}>
          <SubHeading>You’re invited</SubHeading>
          <Body style={{ opacity: 0.85 }}>{lead}</Body>
          <Body style={{ marginTop: theme.spacing(1), opacity: 0.85 }}>
            Sign up or sign in with the same email address—we’ll attach this invitation automatically after
            verification.
          </Body>
          <Body style={{ marginTop: theme.spacing(1), opacity: 0.75 }}>
            This page is linked from your invitation email; you can revisit it anytime.
          </Body>
          <Button label="Continue" onPress={handleContinueToSignIn} />
          <Button label="Not now" variant="outlined" onPress={handleCloseOrHome} />
        </View>
      </Screen>
    );
  }

  if (finalizeError) {
    return (
      <Screen title="Invitation" showOfflineIndicator={false} showBackButton onBack={() => router.replace('/(tabs)')}>
        <SubHeading>Something interrupted this invite</SubHeading>
        <Body>{finalizeError}</Body>
        <View style={{ marginTop: theme.spacing(2), gap: theme.spacing(1) }}>
          <Button label="Try again" onPress={handleRetry} />
          <Button label="Close" variant="outlined" onPress={() => router.replace('/(tabs)')} />
        </View>
      </Screen>
    );
  }

  return (
    <Screen
      title="Invitation"
      scrollable={false}
      showOfflineIndicator={false}
      showBackButton
      onBack={handleCloseOrHome}
    >
      <View style={{ gap: theme.spacing(2), alignItems: 'center', paddingVertical: theme.spacing(3) }}>
        <ActivityIndicator size="large" color={theme.colors.primary} accessibilityLabel="Applying invitation" />
        <Body style={{ opacity: 0.85 }}>Applying your collaborator invitation…</Body>
      </View>
    </Screen>
  );
}
